import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import VariationPicker from '../VariationPicker';
import DetailsLoadingOverlay from '../DetailsLoadingOverlay';
import {formatProductPickerPrice, getProductPrice} from '../modeStorage';
import {useProductFilter} from '../useProductFilter';
import * as S from '../styles';
import {ProductPickResult, ProductPickerItem, ProductPickerVariation} from '../types';
import {resolveProductDetails, willFetchProductDetails} from '../resolveProductDetails';

interface Props {
    products: ProductPickerItem[];
    disabled?: boolean;
    loading?: boolean;
    placeholder?: string;
    autoFocus?: boolean;
    compact?: boolean;
    onPick: (result: ProductPickResult) => void;
    fetchProductDetails?: (productId: number) => Promise<ProductPickerItem | null>;
    forceProductDetails?: boolean;
}

const QuickSearchMode = ({
    products,
    disabled,
    loading,
    placeholder = 'Поиск: название, артикул, бренд, ID',
    autoFocus,
    compact,
    onPick,
    fetchProductDetails,
    forceProductDetails,
}: Props) => {
    const rootRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const [focused, setFocused] = useState(false);
    const [open, setOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const [portalStyle, setPortalStyle] = useState<React.CSSProperties>({});
    const [pendingProduct, setPendingProduct] = useState<ProductPickerItem | null>(null);
    const [pendingVariation, setPendingVariation] = useState<ProductPickerVariation | null>(null);
    const [resolvingProduct, setResolvingProduct] = useState<ProductPickerItem | null>(null);
    const {query, setQuery, results, exactByCode, filterNow, isFiltering} = useProductFilter(products);

    const visible = useMemo(() => results.slice(0, 25), [results]);
    const showDropdown = open && query.trim().length > 0;

    const updatePortalPosition = useCallback(() => {
        const el = rootRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const gap = 4;
        const maxHeight = Math.min(280, Math.max(120, window.innerHeight - rect.bottom - gap - 12));
        setPortalStyle({
            left: rect.left,
            width: Math.max(rect.width, 280),
            top: rect.bottom + gap,
            maxHeight,
        });
    }, []);

    useEffect(() => {
        if (autoFocus) inputRef.current?.focus();
    }, [autoFocus]);

    useEffect(() => {
        setActiveIndex(0);
    }, [query]);

    useEffect(() => {
        if (!showDropdown) return;
        updatePortalPosition();
        const onScroll = () => updatePortalPosition();
        const onResize = () => updatePortalPosition();
        window.addEventListener('scroll', onScroll, true);
        window.addEventListener('resize', onResize);
        return () => {
            window.removeEventListener('scroll', onScroll, true);
            window.removeEventListener('resize', onResize);
        };
    }, [showDropdown, updatePortalPosition, visible.length]);

    useEffect(() => {
        if (!showDropdown) return;
        const onPointerDown = (event: PointerEvent) => {
            const target = event.target as Node;
            if (rootRef.current?.contains(target)) return;
            const dropdown = document.getElementById('product-picker-quick-dropdown');
            if (dropdown?.contains(target)) return;
            setOpen(false);
        };
        document.addEventListener('pointerdown', onPointerDown);
        return () => document.removeEventListener('pointerdown', onPointerDown);
    }, [showDropdown]);

    const commit = (product: ProductPickerItem, variation?: ProductPickerVariation | null) => {
        onPick({
            product,
            variation: variation || null,
            quantity: 1,
            price: variation?.price ?? getProductPrice(product),
        });
        setQuery('');
        setOpen(false);
        setPendingProduct(null);
        setPendingVariation(null);
        inputRef.current?.focus();
    };

    const startPick = async (product: ProductPickerItem) => {
        if (resolvingProduct) return;

        const fetchOptions = {force: forceProductDetails};
        const showLoading = willFetchProductDetails(product, fetchProductDetails, fetchOptions);
        if (showLoading) {
            setResolvingProduct(product);
            setOpen(false);
        }

        try {
            const resolved = await resolveProductDetails(product, fetchProductDetails, fetchOptions);
            if (resolved.has_variation) {
                setPendingProduct(resolved);
                setPendingVariation(null);
                setOpen(false);
                return;
            }
            commit(resolved, null);
        } finally {
            setResolvingProduct(null);
        }
    };

    const onKeyDown = (e: React.KeyboardEvent) => {
        if (!open && (e.key === 'ArrowDown' || e.key === 'Enter') && query.trim()) {
            setOpen(true);
        }

        if (e.key === 'Enter' && query.trim()) {
            e.preventDefault();
            const live = isFiltering
                ? filterNow(query).slice(0, 25)
                : visible;
            const target = live[Math.min(activeIndex, Math.max(live.length - 1, 0))]
                || exactByCode;
            if (target) {
                void startPick(target);
            }
            return;
        }

        if (!showDropdown || visible.length === 0) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIndex((prev) => (prev + 1) % visible.length);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex((prev) => (prev - 1 + visible.length) % visible.length);
        } else if (e.key === 'Escape') {
            setOpen(false);
        }
    };

    const dropdown = showDropdown
        ? createPortal(
            <S.Dropdown
                id="product-picker-quick-dropdown"
                $portal
                style={portalStyle}
            >
                {visible.length === 0 ? (
                    <S.EmptyState>
                        {loading ? 'Загрузка товаров…' : 'Ничего не найдено'}
                    </S.EmptyState>
                ) : (
                    visible.map((product, index) => (
                        <S.ResultRow
                            key={product.id}
                            type="button"
                            $active={index === activeIndex || resolvingProduct?.id === product.id}
                            disabled={Boolean(resolvingProduct)}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => startPick(product)}
                        >
                            <S.ResultMain>
                                <S.ResultTitle>{product.name}</S.ResultTitle>
                                <S.ResultMeta>
                                    {[product.part_number, product.brand, product.has_variation ? 'вариации' : '']
                                        .filter(Boolean)
                                        .join(' · ')}
                                </S.ResultMeta>
                            </S.ResultMain>
                            {resolvingProduct?.id === product.id ? (
                                <S.ResultRowLoading />
                            ) : (
                                <S.ResultPrice>{formatProductPickerPrice(product)}</S.ResultPrice>
                            )}
                        </S.ResultRow>
                    ))
                )}
            </S.Dropdown>,
            document.body,
        )
        : null;

    return (
        <S.Root ref={rootRef} $compact={compact}>
            <S.SearchShell $focused={focused} $compact={compact}>
                <S.SearchInput
                    ref={inputRef}
                    value={query}
                    disabled={disabled || loading}
                    placeholder={placeholder}
                    onFocus={() => {
                        setFocused(true);
                        if (query.trim()) setOpen(true);
                    }}
                    onBlur={() => setFocused(false)}
                    onChange={(e) => {
                        setQuery(e.target.value);
                        setOpen(true);
                    }}
                    onKeyDown={onKeyDown}
                />
            </S.SearchShell>
            {dropdown}
            <S.Hint $compact={compact}>Enter — добавить · Esc — закрыть</S.Hint>

            {resolvingProduct && (
                <DetailsLoadingOverlay
                    product={resolvingProduct}
                    message={
                        resolvingProduct.has_variation
                            ? 'Загрузка вариаций…'
                            : 'Загрузка товара…'
                    }
                />
            )}

            {pendingProduct && (
                <VariationPicker
                    asModal
                    product={pendingProduct}
                    selectedId={pendingVariation?.id}
                    onSelect={setPendingVariation}
                    onCancel={() => {
                        setPendingProduct(null);
                        setPendingVariation(null);
                        inputRef.current?.focus();
                    }}
                    onConfirm={() => pendingVariation && commit(pendingProduct, pendingVariation)}
                    confirmDisabled={!pendingVariation}
                />
            )}
        </S.Root>
    );
};

export default QuickSearchMode;
